// Runtime helpers live in the main @gw/ui package, not in @gw/ui/types (types-only subpath)
import {
  clearError,
  clearErrorStatus,
  getErrorMessage,
  setError,
  setErrorStatus,
  setLoaded,
  setLoading,
} from '@gw/ui';

import type {
  LoadingStates,
  LoadedStates,
  LoadedTimestamps,
  ErrorStates,
  ErrorStatuses,
} from '@gw/ui/types';

interface AsyncSliceActionOptions {
  prefix?: string;
}

/**
 * Creates an async action handler for a Zustand slice that manages loading,
 * loaded, error, and error status states automatically.
 *
 * This helper eliminates the repetitive try/catch + state management pattern
 * used across ~110-120 async actions in 23 Zustand slices.
 *
 * @template TState - The Zustand slice state type
 * @template TResult - The type returned by the async action (defaults to unknown)
 * @param operation - Semantic name for the async operation (e.g., 'fetchItems')
 * @param action - The async function to execute
 * @param onSuccess - Maps the action result to partial state updates
 * @param set - Zustand's set function
 * @param get - Zustand's get function
 * @param options - Optional configuration (e.g., prefix for prefixed state fields)
 * @returns An async function that executes the action with full state management
 */
export function createAsyncSliceAction<TState, TResult = unknown>(
  operation: string,
  action: () => Promise<TResult>,
  onSuccess: (result: TResult) => Partial<TState>,
  set: (partial: Partial<TState>) => void,
  get: () => TState,
  options?: AsyncSliceActionOptions
): () => Promise<void> {
  const prefix = options?.prefix;
  const loadingKey = prefix ? `${prefix}IsLoading` : 'isLoading';
  const loadedKey = prefix ? `${prefix}HasLoaded` : 'hasLoaded';
  const lastLoadedKey = prefix ? `${prefix}LastLoaded` : 'lastLoaded';
  const errorsKey = prefix ? `${prefix}Errors` : 'errors';
  const errorStatusesKey = prefix ? `${prefix}ErrorStatuses` : 'errorStatuses';

  function readField<T>(state: TState, key: string): T {
    return (state as Record<string, unknown>)[key] as T;
  }

  return async () => {
    const state = get();

    // Set loading state and clear previous errors before executing the action
    set({
      [loadingKey]: setLoading(readField<LoadingStates>(state, loadingKey), operation, true),
      [errorsKey]: clearError(readField<ErrorStates>(state, errorsKey), operation),
      [errorStatusesKey]: clearErrorStatus(
        readField<ErrorStatuses>(state, errorStatusesKey),
        operation
      ),
    } as Partial<TState>);

    try {
      const result = await action();
      const successState = onSuccess(result);
      const currentState = get();

      const { hasLoaded, lastLoaded } = setLoaded(
        readField<LoadedStates>(currentState, loadedKey),
        readField<LoadedTimestamps>(currentState, lastLoadedKey),
        operation,
        true
      );

      set({
        ...successState,
        [loadingKey]: setLoading(
          readField<LoadingStates>(currentState, loadingKey),
          operation,
          false
        ),
        [loadedKey]: hasLoaded,
        [lastLoadedKey]: lastLoaded,
      } as Partial<TState>);
    } catch (error) {
      const currentState = get();
      const message = getErrorMessage(error, `Failed to ${operation}`);
      const status =
        error instanceof Error && 'status' in error
          ? (error as { status?: number }).status
          : undefined;

      const { hasLoaded, lastLoaded } = setLoaded(
        readField<LoadedStates>(currentState, loadedKey),
        readField<LoadedTimestamps>(currentState, lastLoadedKey),
        operation,
        true
      );

      set({
        [loadingKey]: setLoading(
          readField<LoadingStates>(currentState, loadingKey),
          operation,
          false
        ),
        [errorsKey]: setError(readField<ErrorStates>(currentState, errorsKey), operation, message),
        [errorStatusesKey]: setErrorStatus(
          readField<ErrorStatuses>(currentState, errorStatusesKey),
          operation,
          status
        ),
        [loadedKey]: hasLoaded,
        [lastLoadedKey]: lastLoaded,
      } as Partial<TState>);
    }
  };
}
